/**
 * The integrations panel: what is connected, and what to do about it.
 *
 * The Project status row is a pure status/navigation row - no connect flow,
 * no two-way sync (spec #191 removed the client PM-tool mirror).
 *
 * The WhatsApp row is the one row that can be absent: it renders only while
 * customer channels are switched on (see `channelRowState`), so with the flag
 * off every brand sees the same rows it saw before.
 *
 * @module web/screens/app/overview/Integrations
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError, del, get } from "../../../api/client.ts";
import type { CustomerChannelsResponse } from "../../../api/types.ts";
import { useBrand } from "../../../state/BrandContext.tsx";
import { channelRowState, errorSentence } from "./customerChannels.ts";
import { clientInviteRow } from "./clientInvites.ts";
import { WhatsAppConnectModal } from "./WhatsAppConnectModal.tsx";

type Tone = "ok" | "todo" | "muted";

export function Integrations() {
  const { brand } = useBrand();
  const [channels, setChannels] = useState<CustomerChannelsResponse | null>(null);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [waOpen, setWaOpen] = useState(false);

  // Fail-closed: no brand, a server without the routes, or any error → null →
  // the row is hidden. Never a phantom "Not connected".
  const brandId = brand?.id ?? null;
  const loadChannels = useCallback(() => {
    if (!brandId) { setChannels(null); return; }
    void get<CustomerChannelsResponse>(`/api/customer-channels?brandId=${encodeURIComponent(brandId)}`)
      .then(d => setChannels(d))
      .catch(() => setChannels(null));
  }, [brandId]);
  useEffect(() => { loadChannels(); }, [loadChannels]);

  const wa = channelRowState(channels);

  const disconnectWhatsApp = async () => {
    if (wa.kind !== "connected") return;
    if (!window.confirm(`Disconnect WhatsApp ${wa.connection.senderMasked}?`)) return;
    setChannelError(null);
    try {
      await del<{ ok: true }>(`/api/customer-channels/${encodeURIComponent(wa.connection.id)}`);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { code?: unknown } | null) : null;
      const code = body && typeof body.code === "string" ? body.code : null;
      setChannelError(errorSentence(e instanceof ApiError ? e.status : 0, code, e instanceof Error ? e.message : null));
    }
    loadChannels();
  };

  const metaOk = !!brand?.metaConnected;
  const invites = clientInviteRow(brand
    ? { slackInvited: !!brand.slackInvited, email: brand.email }
    : null);

  // The folder is the assets. The live page reads `driveFolderId`,
  // `assetsFolder` and `driveFolder` off the brand - none of which the server
  // has ever sent - so this row says "Managed by Aditor" to a brand that
  // attached its Drive folder in the checklist directly above. The checklist's
  // own `content` item is the same fact, already on the wire. See §13, pattern A.
  const hasAssets = !!brand?.checklist?.items.find(i => i.id === "content")?.done;

  return (
    <>
      <div className="rule fade-in" style={{ margin: "24px 0 12px", animationDelay: ".05s" }}>Your integrations</div>
      <section className="integrations fade-in" id="integrations-card" style={{ animationDelay: ".08s" }}>
        <div className="integrations-list" id="integrations-list">
          <Row
            tone={metaOk ? "ok" : "todo"}
            title="Meta Ads"
            desc={metaOk
              ? "Automatic iterations on your winning ads"
              : "Connect your Meta account to enable ad reporting & iterations"}
            status={metaOk ? "Connected" : "Not connected"}
            action={metaOk ? "Manage" : "Connect"}
            primary={!metaOk}
            onAction={() => { window.location.hash = "meta"; }}
            icon={
              <>
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </>
            }
          />

          {invites && (
            <Row
              tone="ok"
              title="Slack"
              desc={invites.desc}
              status={invites.status}
              icon={<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>}
            />
          )}

          {wa.kind !== "hidden" && (
            <Row
              tone={wa.kind === "connected" ? "ok" : wa.kind === "disconnected" ? "todo" : "muted"}
              title="Delivery channels"
              desc={channelError ?? (
                wa.kind === "connected"
                  ? wa.sends
                    ? `WhatsApp ${wa.connection.senderMasked} gets every finished video.`
                    : `WhatsApp ${wa.connection.senderMasked} is connected. Sending starts once we switch it on.`
                  : wa.kind === "personal_needed" ? "Sign in with Google to choose where finished videos go."
                    : wa.kind === "unavailable" ? "Not switched on for this workspace yet."
                      : "Where finished videos go. WhatsApp first; e-mail and teammates next."
              )}
              status={wa.kind === "connected" ? "Connected"
                : wa.kind === "personal_needed" ? "Sign in first"
                  : wa.kind === "unavailable" ? "Coming soon"
                    : "Not connected"}
              {...(wa.kind === "connected"
                ? { action: "Disconnect", onAction: () => { void disconnectWhatsApp(); } }
                : wa.kind === "disconnected"
                  ? { action: "Connect", primary: true, onAction: () => { setChannelError(null); setWaOpen(true); } }
                  : wa.kind === "personal_needed"
                    // The row used to tell people to sign in and give them no way
                    // to. The OAuth start now adopts the brand from the session
                    // they are already holding, so a plain link is enough — they
                    // come back to this same brand, not to the signup wizard.
                    ? { action: "Sign in", primary: true, onAction: () => { window.location.href = "/api/auth/google/start"; } }
                    : {})}
              icon={<><path d="M22 2 11 13" /><path d="M22 2 15 22 11 13 2 9z" /></>}
            />
          )}

          <Row
            tone="muted"
            title="Project status"
            desc="See where every briefing is, from editor search to delivery."
            action="Open board"
            onAction={() => { window.location.hash = "board"; }}
            icon={
              <>
                <rect x="3" y="3" width="7" height="18" rx="1.5" />
                <rect x="14" y="3" width="7" height="12" rx="1.5" />
              </>
            }
          />

          <Row
            tone={hasAssets ? "ok" : "muted"}
            title="Brand Assets"
            desc={hasAssets
              ? "Your brand assets are synced from Google Drive"
              : "Your Aditor team manages brand assets in Drive for you"}
            status={hasAssets ? "Linked" : "Managed by Aditor"}
            icon={<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />}
          />
        </div>
      </section>
      {brandId && (
        <WhatsAppConnectModal
          open={waOpen}
          brandId={brandId}
          onClose={() => setWaOpen(false)}
          onConnected={loadChannels}
        />
      )}
    </>
  );
}

function Row({ tone, title, desc, status, action, primary, onAction, icon }: {
  tone: Tone;
  title: string;
  desc: string;
  status?: string;
  action?: string;
  primary?: boolean;
  onAction?: () => void;
  icon: React.ReactNode;
}) {
  return (
    <div className="integration-row">
      <div className={`integration-icon ${tone}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {icon}
        </svg>
      </div>
      <div className="integration-info">
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
      {status && (
        <span className={`integration-status ${tone}`}><span className="dot" />{status}</span>
      )}
      {action && (
        <button className={`integration-action${primary ? " primary" : ""}`} onClick={onAction}>{action}</button>
      )}
    </div>
  );
}
